/**
 * SketchRepository - Repositório local para Croquis 2D no IndexedDB
 * Permite salvar, atualizar e consultar pranchas, páginas e elementos 100% offline.
 */

import { openDatabase } from './IndexedDBSetup';

const STORE_NAME = 'sketchProjects';
const ELEMENTS_STORE = 'sketchElements';

export const SketchRepository = {
  /**
   * Salva ou cria um projeto de croqui localmente
   */
  async saveProject(projectData) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      const id = String(projectData.id || projectData.localId || `local_sketch_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`);
      const record = {
        ...projectData,
        id,
        localId: projectData.localId || id,
        serverId: projectData.serverId || (String(projectData.id).startsWith('local_') ? null : projectData.id),
        syncStatus: projectData.syncStatus || (String(projectData.id).startsWith('local_') ? 'pending' : 'synced'),
        updatedAt: new Date().toISOString()
      };

      const request = store.put(record);
      request.onsuccess = () => resolve(record);
      request.onerror = () => reject(new Error(`Erro ao salvar croqui no IndexedDB: ${request.error}`));
    });
  },

  /**
   * Busca um projeto de croqui por ID (seja ID numérico do servidor ou ID local)
   */
  async getProjectById(id) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const strId = String(id);

      // Tenta direto pela chave primária
      const req = store.get(strId);
      req.onsuccess = () => {
        if (req.result) {
          return resolve(req.result);
        }

        // Tenta pelo índice serverId
        const serverIndex = store.index('serverId');
        const numId = Number(id);
        const serverReq = serverIndex.get(isNaN(numId) ? strId : numId);
        serverReq.onsuccess = () => {
          if (serverReq.result) return resolve(serverReq.result);

          // Tenta pelo índice localId
          const localIndex = store.index('localId');
          const localReq = localIndex.get(strId);
          localReq.onsuccess = () => resolve(localReq.result || null);
          localReq.onerror = () => resolve(null);
        };
        serverReq.onerror = () => resolve(null);
      };
      req.onerror = () => reject(new Error(`Erro ao buscar croqui: ${req.error}`));
    });
  },

  /**
   * Atualiza dados parciais do croqui (ex: canvas_data, snapshot)
   */
  async updateProject(id, updateData) {
    const existing = await this.getProjectById(id);
    const updated = {
      ...(existing || {}),
      ...updateData,
      id: existing ? existing.id : String(id),
      syncStatus: 'pending',
      updatedAt: new Date().toISOString()
    };
    return this.saveProject(updated);
  },

  /**
   * Salva elementos do croqui localmente
   */
  async saveElements(projectId, elements) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([ELEMENTS_STORE], 'readwrite');
      const store = transaction.objectStore(ELEMENTS_STORE);

      const record = {
        id: `elem_${projectId}`,
        projectId: String(projectId),
        elements,
        syncStatus: 'pending',
        updatedAt: new Date().toISOString()
      };

      const request = store.put(record);
      request.onsuccess = () => resolve(record);
      request.onerror = () => reject(new Error(`Erro ao salvar elementos: ${request.error}`));
    });
  },

  /**
   * Busca elementos de um projeto
   */
  async getElements(projectId) {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([ELEMENTS_STORE], 'readonly');
      const store = transaction.objectStore(ELEMENTS_STORE);
      const req = store.get(`elem_${projectId}`);
      req.onsuccess = () => resolve(req.result?.elements || []);
      req.onerror = () => reject(new Error(`Erro ao buscar elementos: ${req.error}`));
    });
  },

  /**
   * Salva imagem de preview / snapshot
   */
  async saveSnapshot(projectId, snapshotDataUrl) {
    return this.updateProject(projectId, {
      snapshotDataUrl,
      hasSnapshotPending: true
    });
  }
};

export default SketchRepository;
